import {
  EQUIP_SLOTS,
  aggregateWeaponStats,
  applyInventoryOp,
  canEquipInSlot,
  countAmmo,
  countReserveKnown,
  createDefaultLoadout,
  findFreePlacement,
  fittedMagazine,
  fittedModsDeep,
  fittedTacticalKind,
  findItemAnywhere,
  getItemDef,
  hasSuppressor,
  inventoryGrids,
  loadedAmmo,
  opticZoom,
  recoilMultiplier,
  totalWeight,
  weaponWithAmmo,
  type AmmoItemDef,
  type CaliberId,
  type EffectiveWeapon,
  type Inventory,
  type InvContext,
  type InventoryOp,
  type ItemInstance,
  type Snapshot,
  type TacticalKind,
  type WeaponItemDef,
  type WeaponEquipSlot,
} from "@rietz/shared";
import { NetClock } from "../net/clock.js";
import type { ContainerStateMsg, GameSocket, InvResultMsg } from "../net/socket.js";
import { OpIds } from "./opIds.js";
import { resetContainerSearch } from "../ui/inventory/search.js";
import {
  setContainer,
  setInvOpen,
  setInventory,
  setMedCast,
  type OpenContainer,
} from "../ui/store.js";

/**
 * The inventory as the client believes it, INSIDE A RAID — the game side of
 * the UI bridge (`game/metaInventory.ts` is the between-raids twin).
 *
 * Two layers: AUTHORITATIVE (the last `inv`/`containerState` plus every
 * confirmed op) and DISPLAYED (authoritative with every unanswered op
 * re-applied through the same shared `applyInventoryOp`). A drag is visible
 * the frame the mouse is released; a wrong guess gets `invResult{ok:false}`
 * plus a fresh `inv`, the failed op is dropped and the survivors re-applied
 * (one that no longer applies is refused by the op itself).
 *
 * A `split` mints provisional ids `c1`, `c2`, … (the prefix cannot collide),
 * living until the next `inv`; each pending op remembers its ids so a replay
 * reproduces them. Offline, every op goes straight into the authoritative
 * state; containers, dropped items and meds are inert.
 */

/** One op the server has not answered yet. */
interface PendingOp {
  /** Drawn from the connection's shared counter once the op is worth sending. */
  opId: number;
  op: InventoryOp;
  /** Instance ids this op minted, in mint order; a replay re-uses them. */
  readonly iids: string[];
}

/** Identity of the open container. `authContainer` carries its grid. */
interface ContainerMeta {
  id: string;
  kind: OpenContainer["kind"];
  name: string;
}

/**
 * Med-bar drift tolerated before the store is rewritten — re-deriving `endMs`
 * from every snapshot moves by a few ms and must not re-render React 15 Hz.
 */
const MED_END_TOLERANCE_MS = 25;

export interface InventoryControllerOptions {
  /** `null` offline: everything is local and nothing is sent. */
  socket: GameSocket | null;
  clock: NetClock;
  offline: boolean;
  /** THE connection's op-id counter, shared with the menu and the traders. */
  opIds: OpIds;
  /**
   * The inventory screen opened or closed — an input-mode change `main.ts`
   * owns, since death has a claim on the cursor too.
   */
  onScreenChanged?: (open: boolean) => void;
  /**
   * The displayed inventory changed; here is its weight. Pushed rather than
   * pulled because it fires from the constructor, before `main.ts` has assigned
   * the controller (a pull would be a TDZ crash). Fires on optimistic changes
   * too — the prediction should believe what the player sees.
   */
  onWeightChanged?: (carriedKg: number) => void;
}

export class InventoryController {
  private readonly o: InventoryControllerOptions;

  // --- Authoritative -------------------------------------------------------
  private authInv: Inventory | null = null;
  private authContainer: InvContext["container"] = null;
  private containerMeta: ContainerMeta | null = null;

  // --- Optimistic ----------------------------------------------------------
  private readonly pending: PendingOp[] = [];
  private displayed: InvContext | null = null;
  private localIidSeq = 0;

  // --- Screen / med cast ---------------------------------------------------
  private screenOpen = false;
  /** Duration of the running cast, kept so the authoritative correction can
   *  keep drawing the same bar instead of guessing a length. */
  private medDurMs = 0;
  private medEndMs: number | null = null;

  // --- Derived, recomputed on every inventory change (never per frame) ------
  private weaponItem: ItemInstance | null = null;
  private weaponDefCache: WeaponItemDef | null = null;
  private ammoDefCache: AmmoItemDef | null = null;
  private effCache: EffectiveWeapon | null = null;
  private reserve = 0;
  /** At least one compatible spare magazine has a count the player does not
   *  know (protocol v27) — the HUD's "+?" beside the reserve. */
  private reserveUnknownCache = false;
  private weaponKeyCache = "";
  /** Fitted attachment def ids, in `def.slots` order. Empty for a bare gun. */
  private modDefIdsCache: readonly string[] = [];
  private recoilMultCache = 1;
  private suppressedCache = false;
  private zoomCache: number | null = null;
  /** Which mounted device the equipped gun carries, or `null` for a bare rail. */
  private tacticalCache: TacticalKind | null = null;
  /** Kilograms of everything worn and carried. Drives the encumbrance mods the
   *  local prediction steps with, so it has to be as fresh as the panel. */
  private carriedKgCache = 0;
  private activeWeaponSlot: WeaponEquipSlot = "weapon";

  constructor(opts: InventoryControllerOptions) {
    this.o = opts;
    if (opts.offline) {
      // The same loadout the server would have handed us, minted with local
      // ids — offline is a full-fidelity single-player sandbox, not a stub.
      this.authInv = createDefaultLoadout(this.mintIid);
    }
    this.rebuild();
  }

  // -----------------------------------------------------------------------
  // Equipped weapon — what `fireControl` shoots with
  // -----------------------------------------------------------------------

  /** The equipped weapon instance, or `null` when empty-handed. */
  get weaponInstance(): ItemInstance | null {
    return this.weaponItem;
  }

  /** Switches hands immediately; the server receives the same selected slot. */
  selectWeapon(slot: WeaponEquipSlot): void {
    if (slot === this.activeWeaponSlot) return;
    this.activeWeaponSlot = slot;
    this.refreshEquipped();
    this.o.socket?.sendWeaponSelect(slot);
  }

  /** Reasserts the local hands slot after a welcome/reconnect. */
  syncWeaponSelection(): void {
    this.o.socket?.sendWeaponSelect(this.activeWeaponSlot);
  }

  get weaponDef(): WeaponItemDef | null {
    return this.weaponDefCache;
  }

  /** Everything worn and carried, in kilograms. Recomputed on every inventory
   *  change, never per frame — see `onWeightChanged`. */
  get carriedKg(): number {
    return this.carriedKgCache;
  }

  /** The cartridge currently in the magazine, or `null` when it is empty. */
  get loadedAmmoDef(): AmmoItemDef | null {
    return this.ammoDefCache;
  }

  /**
   * Gun, attachments and cartridge folded together — attachments first, then
   * the cartridge, as the shared module documents.
   */
  get effectiveWeapon(): EffectiveWeapon | null {
    return this.effCache;
  }

  /**
   * Def ids of every attachment on the equipped gun, deep, in fold order — the
   * list the viewmodel builds its `"missing"` view from. Through the shared
   * `fittedModsDeep`, so a part the aggregation refuses is one the model does
   * not grow: the gun seen is the gun shot with.
   */
  get weaponModDefIds(): readonly string[] {
    return this.modDefIdsCache;
  }

  /** Product of every fitted `recoilMult` — the viewmodel's kick scale. */
  get weaponRecoilMult(): number {
    return this.recoilMultCache;
  }

  /** True when a can is screwed on. Drives the LOCAL shot synthesis only. */
  get weaponSuppressed(): boolean {
    return this.suppressedCache;
  }

  /** Magnification of the fitted optic, or `null` for iron sights. */
  get weaponZoom(): number | null {
    return this.zoomCache;
  }

  /**
   * The mounted device on the equipped gun, or `null`. Cached per weapon change
   * — the 1P effect asks every frame. Whether it is ON is `Input.tacticalOn`.
   */
  get weaponTacticalKind(): TacticalKind | null {
    return this.tacticalCache;
  }

  /**
   * A cheap identity for "the gun changed": instance, def, cartridge and fitted
   * parts (deep). The mods half is a signature over contents, not the instance
   * reference, so a bare resync producing an identical gun does not rebuild
   * meshes. Callers cache per-weapon derivations against this.
   */
  get weaponKey(): string {
    return this.weaponKeyCache;
  }

  /**
   * What the next reload can reach and the player can COUNT — the HUD's "/ 90".
   * See `refreshEquipped`.
   */
  get reserveRounds(): number {
    return this.reserve;
  }

  /**
   * Some reserve is in a box nobody has looked into — the HUD's "+?". Always
   * false for the shotgun's loose shells.
   */
  get reserveUnknown(): boolean {
    return this.reserveUnknownCache;
  }

  /** The inventory as displayed. `null` before the first `inv`. */
  get inventory(): Inventory | null {
    return this.displayed?.inv ?? null;
  }

  get isScreenOpen(): boolean {
    return this.screenOpen;
  }

  /** Id of the open container, or `null`. Used by the interact prompt. */
  get openContainerId(): string | null {
    return this.containerMeta?.id ?? null;
  }

  // -----------------------------------------------------------------------
  // UiActions
  // -----------------------------------------------------------------------

  tryOp(op: InventoryOp): void {
    const ctx = this.displayed;
    if (ctx === null) return;

    // The id is drawn only once the op is worth sending: a locally refused op
    // must not put a hole in the strictly-increasing sequence.
    const p: PendingOp = { opId: 0, op, iids: [] };
    const next = this.applyPending(ctx, p);
    // Refused against the state the player is LOOKING at: there is nothing to
    // predict and nothing worth asking the server about.
    if (next === null) return;

    if (this.o.offline) {
      // No confirmation is ever coming, so the guess is the truth.
      this.authInv = next.inv;
      this.authContainer = next.container;
      this.displayed = next;
      this.publish();
      return;
    }

    p.opId = this.o.opIds.next();
    this.pending.push(p);
    this.displayed = next;
    this.publish();
    this.o.socket?.sendInvOp(p.opId, op);
  }

  useMed(iid: string): void {
    if (this.o.offline) return;
    const ctx = this.displayed;
    if (ctx === null) return;

    const found = findItemAnywhere(ctx.inv, iid);
    if (found === null) return;
    const def = getItemDef(found.placed.item.defId);
    // A med or a grocery: eating is the same verb on the body as bandaging
    // (`useMed` carries both, one cast slot). Groceries stay `kind: "loot"`,
    // hence the test on `consume`.
    if (def === null) return;
    const useTimeSec =
      def.kind === "med"
        ? def.useTimeSec
        : def.kind === "loot"
          ? def.consume?.useTimeSec
          : undefined;
    if (useTimeSec === undefined) return;

    this.o.socket?.sendUseMed(iid);

    // Optimistic bar: the server may refuse (nothing to treat/gain), in which
    // case the next snapshot carries no `medEndTick` and the bar clears. A
    // two-second animation that starts after a round trip feels like a dropped
    // key.
    const durMs = useTimeSec * 1000;
    this.medDurMs = durMs;
    this.medEndMs = performance.now() + durMs;
    setMedCast({ endMs: this.medEndMs, durMs });
  }

  closeContainer(): void {
    const meta = this.containerMeta;
    if (meta === null) return;
    this.containerMeta = null;
    this.authContainer = null;
    if (!this.o.offline) this.o.socket?.sendClose(meta.id);
    // Pending ops that reached into the container cannot apply any more;
    // `rebuild` drops them on the spot.
    this.rebuild();
  }

  setInventoryOpen(open: boolean): void {
    if (this.screenOpen === open) return;
    this.screenOpen = open;
    setInvOpen(open);
    // Walking away from a crate closes it; so does closing the screen it was
    // being shown in. The loot panel has no life of its own.
    if (!open) this.closeContainer();
    this.o.onScreenChanged?.(open);
  }

  // -----------------------------------------------------------------------
  // World actions (driven by `game/interact.ts`)
  // -----------------------------------------------------------------------

  /** Opens a world container and shows it — the loot panel shares the screen
   *  with the inventory, because looting is a transfer between the two. */
  openContainer(containerId: string): void {
    if (this.o.offline) return;
    this.o.socket?.sendOpen(containerId);
    this.setInventoryOpen(true);
  }

  /** Takes a dropped item off the ground. The server auto-places it in
   *  canonical scan order and answers with a fresh `inv`. */
  pickup(itemId: string): void {
    if (this.o.offline) return;
    this.o.socket?.sendPickup(itemId);
  }

  /**
   * Would a pickup of `defId` find a home? Mirrors `tryPickUp`'s placement
   * against the AUTHORITATIVE inventory (an in-flight drag is not room the
   * server knows). Answers yes when it cannot tell — the E press goes out anyway.
   */
  canHold(defId: string): boolean {
    const inv = this.authInv;
    if (inv === null) return true;
    const def = getItemDef(defId);
    if (def === null) return true;

    for (const { grid } of inventoryGrids(inv)) {
      if (findFreePlacement(grid, def) !== null) return true;
    }
    for (const slot of EQUIP_SLOTS) {
      if (canEquipInSlot(def, slot) && inv.equipment[slot] === null) return true;
    }
    return false;
  }

  // -----------------------------------------------------------------------
  // Server messages
  // -----------------------------------------------------------------------

  /** A fresh authoritative inventory. Always whole — never a delta. */
  onInv(inv: Inventory): void {
    this.authInv = inv;
    this.rebuild();
  }

  onContainerState(msg: ContainerStateMsg): void {
    // The screen is no longer up: the `open` was in flight when the player
    // closed it. Stand down as a viewer rather than park a panel for the next
    // Tab.
    if (!this.screenOpen) {
      if (!this.o.offline) this.o.socket?.sendClose(msg.id);
      return;
    }
    this.containerMeta = { id: msg.id, kind: msg.kind, name: msg.name };
    // A body brings its worn slots; the key's PRESENCE is what marks a corpse,
    // so it is spread rather than written.
    this.authContainer = {
      id: msg.id,
      grid: msg.grid,
      ...(msg.equipment === undefined ? {} : { equipment: msg.equipment }),
    };
    this.rebuild();
  }

  onInvResult(msg: InvResultMsg): void {
    const idx = this.pending.findIndex((p) => p.opId === msg.opId);
    if (idx < 0) return;
    const p = this.pending[idx] as PendingOp;
    this.pending.splice(idx, 1);

    if (msg.ok && this.authInv !== null) {
      // A successful op gets no resync, so it is folded into the authoritative
      // copy here. Results arrive in send order. A failed fold is left out and
      // the next `inv` corrects everything.
      const folded = this.applyPending(
        { inv: this.authInv, container: this.authContainer },
        p,
      );
      if (folded !== null) {
        this.authInv = folded.inv;
        this.authContainer = folded.container;
      }
    }
    // On `ok: false` forget the guess; the `inv` alongside carries the truth.
    this.rebuild();
  }

  /**
   * One snapshot's med state. `medEndTick` is on the server timeline, so the
   * bar ends at the right moment however late we heard.
   */
  onSnapshot(snap: Snapshot, nowMs: number): void {
    const endTick = snap.you.medEndTick;
    if (endTick === undefined) {
      if (this.medEndMs !== null) {
        this.medEndMs = null;
        this.medDurMs = 0;
        setMedCast(null);
      }
      return;
    }
    // Without a synced clock there is no tick timeline to convert against;
    // the optimistic bar from `useMed` is the better answer until there is.
    if (!this.o.clock.synced) return;

    const endMs =
      nowMs + (NetClock.tickToMs(endTick) - this.o.clock.estServerNowMs(nowMs));
    const prev = this.medEndMs;
    if (prev !== null && Math.abs(prev - endMs) < MED_END_TOLERANCE_MS) return;

    // A cast we never started locally (a reconnect mid-animation) has no known
    // duration; the remaining time is the honest fallback for the bar.
    const durMs = this.medDurMs > 0 ? this.medDurMs : Math.max(endMs - nowMs, 1);
    this.medDurMs = durMs;
    this.medEndMs = endMs;
    setMedCast({ endMs, durMs });
  }

  /** We died: the crate, the bandage and every unanswered op die with us. */
  onDeath(): void {
    this.pending.length = 0;
    this.containerMeta = null;
    this.authContainer = null;
    this.medEndMs = null;
    this.medDurMs = 0;
    setMedCast(null);
    this.setInventoryOpen(false);
    this.rebuild();
  }

  /**
   * Disconnect or map change: forget the raid. Offline keeps its loadout.
   * Searched crates are forgotten HERE only — spawn ids are deterministic, so
   * `crate-17` is a different crate next raid; not in `onDeath`, since a dev
   * respawn stays in the same raid.
   */
  reset(): void {
    resetContainerSearch();
    this.pending.length = 0;
    if (!this.o.offline) this.authInv = null;
    this.authContainer = null;
    this.containerMeta = null;
    this.medEndMs = null;
    this.medDurMs = 0;
    setMedCast(null);
    this.setInventoryOpen(false);
    this.rebuild();
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  /** Mints a provisional instance id. The `c` prefix is what keeps it from
   *  ever colliding with a server-minted one. */
  private readonly mintIid = (): string => {
    this.localIidSeq += 1;
    return `c${this.localIidSeq}`;
  };

  /**
   * Applies one pending op to `ctx`, re-using the ids it minted. `null` when it
   * no longer applies.
   */
  private applyPending(ctx: InvContext, p: PendingOp): InvContext | null {
    let i = 0;
    const nextIid = (): string => {
      const remembered = p.iids[i];
      i += 1;
      if (remembered !== undefined) return remembered;
      const minted = this.mintIid();
      p.iids.push(minted);
      return minted;
    };
    const res = applyInventoryOp(ctx, p.op, nextIid);
    return res.ok ? res.ctx : null;
  }

  /** Authoritative state + every surviving pending op = what is on screen. */
  private rebuild(): void {
    const inv = this.authInv;
    if (inv === null) {
      this.displayed = null;
      this.publish();
      return;
    }

    let ctx: InvContext = { inv, container: this.authContainer };
    for (let i = 0; i < this.pending.length; ) {
      const next = this.applyPending(ctx, this.pending[i] as PendingOp);
      if (next === null) {
        this.pending.splice(i, 1);
        continue;
      }
      ctx = next;
      i += 1;
    }
    this.displayed = ctx;
    this.publish();
  }

  /**
   * Pushes the displayed state into the store and refreshes caches. Once per
   * CHANGE, never per frame.
   */
  private publish(): void {
    const ctx = this.displayed;
    setInventory(ctx?.inv ?? null);

    const meta = this.containerMeta;
    const container = ctx === null ? null : ctx.container;
    if (meta === null || container === null) {
      setContainer(null);
    } else {
      setContainer({
        id: meta.id,
        kind: meta.kind,
        name: meta.name,
        grid: container.grid,
        ...(container.equipment === undefined ? {} : { equipment: container.equipment }),
      });
    }

    this.refreshEquipped();
  }

  private refreshEquipped(): void {
    const inv = this.displayed?.inv ?? null;
    const item = inv === null ? null : inv.equipment[this.activeWeaponSlot];
    this.weaponItem = item;

    const rawDef = item === null ? null : getItemDef(item.defId);
    const def = rawDef !== null && rawDef.kind === "weapon" ? rawDef : null;
    this.weaponDefCache = def;

    // What is chambered is a question about the magazine in the gun —
    // `loadedAmmo`, the same accessor the server's fire path uses; `null` for
    // empty magazine, empty well or unknown cartridge.
    this.ammoDefCache = def === null ? null : loadedAmmo(def, item);

    // Attachments first, cartridge second — see `aggregateWeaponStats`.
    if (def === null) {
      this.effCache = null;
      this.modDefIdsCache = [];
      this.recoilMultCache = 1;
      this.suppressedCache = false;
      this.zoomCache = null;
      this.tacticalCache = null;
    } else {
      this.effCache = weaponWithAmmo(aggregateWeaponStats(def, item), this.ammoDefCache);
      // Each shared accessor walks the tree itself — once per CHANGE, worth it
      // to keep every number coming from the authoritative function.
      this.modDefIdsCache = fittedModsDeep(def, item).map((f) => f.def.id);
      this.recoilMultCache = recoilMultiplier(def, item);
      this.suppressedCache = hasSuppressor(def, item);
      this.zoomCache = opticZoom(def, item);
      // The function the server publishes `tac` with, so both sides agree.
      this.tacticalCache = fittedTacticalKind(def, item);
    }

    // THE RESERVE IS WHAT THE NEXT RELOAD CAN REACH. SWAP (every gun but the
    // shotgun) spends a magazine, so loose rounds are not reserve: rounds in
    // compatible spare magazines. TOPUP (the shotgun) stuffs loose shells: the
    // loaded cartridge exactly, or the whole caliber pool when the tube is
    // empty. And it is what the player KNOWS they can reach: the swap branch
    // counts only boxes with a known count (`countReserveKnown`) and reports
    // the rest as "+?". Both topup branches are known — a stack is visible.
    this.reserve = 0;
    this.reserveUnknownCache = false;
    if (inv !== null && def !== null) {
      if (def.reloadStyle !== "topup") {
        const r = countReserveKnown(inv, def);
        this.reserve = r.rounds;
        this.reserveUnknownCache = r.unknownMags > 0;
      } else if (this.ammoDefCache !== null) {
        this.reserve = countAmmo(inv, this.ammoDefCache.id);
      } else {
        this.reserve = countCaliber(inv, def.caliber);
      }
    }

    // The magazine is part of the identity by its own instance (a weapon's
    // `ammoDefId` is undefined since v12): its iid, def and cartridge all
    // change
    // what `FireControl.syncWeapon` re-derives. The ROUND COUNT deliberately
    // does not — it changes every shot and would rebuild meshes per trigger
    // pull.
    const fittedMag = def === null || item === null ? null : fittedMagazine(def, item);
    const magKey =
      fittedMag === null
        ? ""
        : `${fittedMag.item.iid}:${fittedMag.item.defId}:${fittedMag.item.ammoDefId ?? ""}`;
    this.weaponKeyCache =
      item === null ? "" : `${item.iid}|${item.defId}|${magKey}|${this.modDefIdsCache.join(",")}`;

    // The same function the server weighs with, so the prediction agrees with
    // the authority.
    this.carriedKgCache = inv === null ? 0 : totalWeight(inv);
    this.o.onWeightChanged?.(this.carriedKgCache);
  }
}

/** Total rounds of any cartridge of `caliber` in the player's grids. */
function countCaliber(inv: Inventory, caliber: CaliberId): number {
  let n = 0;
  for (const { grid } of inventoryGrids(inv)) {
    for (const placed of grid.items) {
      const def = getItemDef(placed.item.defId);
      if (def !== null && def.kind === "ammo" && def.caliber === caliber) {
        n += placed.item.stack;
      }
    }
  }
  return n;
}
